/**
 * lcw-cross-sell
 * Custom element vanilla pra:
 *  - carrossel scroll-snap (prev/next, estados disabled)
 *  - fallback opcional pra engine de recomendacoes (fetch lazy via IntersectionObserver)
 *  - tracking GA4: view_item_list, select_item, add_to_cart (item_list_id = cross_sell_pdp)
 *
 * O ADD-TO-CART NAO E FEITO AQUI. Quem adiciona ao carrinho e abre o drawer e o
 * <product-form-component> nativo do Horizon (markup no card). Este JS so escuta o
 * submit pra disparar GA4, sem tocar no fluxo nativo (sem preventDefault).
 *
 * Vai em: assets/lcw-cross-sell.js
 */
(function () {
  'use strict';

  class LcwCrossSell extends HTMLElement {
    connectedCallback() {
      this._viewedFired = false;
      this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.dataset.needsEngine === 'true') {
        this._observeForEngine();
      } else {
        this._init();
      }
    }

    disconnectedCallback() {
      this._engineObserver?.disconnect();
      this._viewObserver?.disconnect();
    }

    /* ---------- init pos-render ---------- */
    _init() {
      this.track = this.querySelector('[data-cs-track]');
      if (!this.track) return;

      this._bindNav();
      this._bindAtcTracking();
      this._bindSelectTracking();
      this._observeForView();
    }

    /* ---------- carrossel ---------- */
    _bindNav() {
      const prev = this.querySelectorAll('[data-cs-prev]');
      const next = this.querySelectorAll('[data-cs-next]');
      const step = () => {
        const card = this.track.querySelector('.lcw-cs__card');
        if (!card) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
        return card.getBoundingClientRect().width + gap;
      };

      prev.forEach((b) =>
        b.addEventListener('click', () =>
          this.track.scrollBy({ left: -step(), behavior: this._reduceMotion ? 'auto' : 'smooth' })
        )
      );
      next.forEach((b) =>
        b.addEventListener('click', () =>
          this.track.scrollBy({ left: step(), behavior: this._reduceMotion ? 'auto' : 'smooth' })
        )
      );

      this._updateNav();
      this.track.addEventListener('scroll', () => this._updateNav(), { passive: true });
      window.addEventListener('resize', () => this._updateNav(), { passive: true });
    }

    _updateNav() {
      const max = this.track.scrollWidth - this.track.clientWidth - 1;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= max;
      this.querySelectorAll('[data-cs-prev]').forEach((b) => (b.disabled = atStart));
      this.querySelectorAll('[data-cs-next]').forEach((b) => (b.disabled = atEnd));
    }

    /* ---------- add to cart: SO tracking, fluxo e nativo ---------- */
    _bindAtcTracking() {
      this.querySelectorAll('[data-cs-atc]').forEach((form) => {
        form.addEventListener('submit', () => {
          // Nao previne o default: o product-form-component nativo cuida do add + drawer.
          const card = form.closest('[data-cs-item]');
          form.querySelector('[data-cs-atc-btn]')?.classList.add('is-loading');
          if (this.dataset.trackAtc === 'true') this._trackAddToCart(card);
        });
      });
    }

    /* ---------- engine fallback ---------- */
    _observeForEngine() {
      this._engineObserver = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0]?.isIntersecting) return;
          obs.disconnect();
          this._fetchEngine();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this._engineObserver.observe(this);
    }

    async _fetchEngine() {
      const { url, productId, sectionId, intent } = this.dataset;
      if (!url || !productId || !sectionId) return this._init();

      const reqUrl = `${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;
      try {
        const res = await fetch(reqUrl);
        if (!res.ok) throw new Error(`status ${res.status}`);
        const text = await res.text();

        const parsed = new DOMParser().parseFromString(text, 'text/html');
        const fresh = parsed.getElementById(this.id);

        if (fresh && fresh.querySelector('[data-cs-track]')) {
          // Injeta o HTML re-renderizado. Os <product-form-component> internos
          // sobem (upgrade) sozinhos ao entrar no DOM vivo.
          this.innerHTML = fresh.innerHTML;
          this.removeAttribute('data-needs-engine');
          this._init();
        } else {
          // Sem recomendacoes: remove o bloco vazio.
          this.remove();
        }
      } catch (err) {
        console.error('[lcw-cross-sell] engine fetch:', err);
        this.remove();
      }
    }

    /* ---------- GA4 ---------- */
    _gaItems() {
      return [...this.querySelectorAll('[data-cs-item]')].map((el, i) => this._itemFromCard(el, i));
    }

    _itemFromCard(el, fallbackIndex) {
      return {
        item_id: el.dataset.productId,
        item_name: el.dataset.productTitle,
        price: Number(el.dataset.productPrice || 0) / 100,
        index: Number(el.dataset.index ?? fallbackIndex ?? 0),
        item_list_id: this.dataset.listId,
        item_list_name: this.dataset.listName,
        quantity: 1,
      };
    }

    _push(eventName, params) {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      } else {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: eventName, ...params });
      }
    }

    _observeForView() {
      this._viewObserver = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0]?.isIntersecting || this._viewedFired) return;
          this._viewedFired = true;
          obs.disconnect();
          this._push('view_item_list', {
            item_list_id: this.dataset.listId,
            item_list_name: this.dataset.listName,
            items: this._gaItems(),
          });
        },
        { threshold: 0.3 }
      );
      this._viewObserver.observe(this);
    }

    _bindSelectTracking() {
      this.querySelectorAll('[data-cs-link]').forEach((link) => {
        link.addEventListener('click', () => {
          const card = link.closest('[data-cs-item]');
          if (!card) return;
          this._push('select_item', {
            item_list_id: this.dataset.listId,
            item_list_name: this.dataset.listName,
            items: [this._itemFromCard(card)],
          });
        });
      });
    }

    _trackAddToCart(card) {
      if (!card) return;
      this._push('add_to_cart', {
        currency: window.Shopify?.currency?.active || 'BRL',
        value: Number(card.dataset.productPrice || 0) / 100,
        items: [this._itemFromCard(card)],
      });
    }
  }

  if (!customElements.get('lcw-cross-sell')) {
    customElements.define('lcw-cross-sell', LcwCrossSell);
  }
})();
